import { Button, Card, Space, Spin, Table, Tag, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useRunStatus } from '../../../api/flagAdmin'

const getStatusColor = (status: string) => {
  switch (status) {
    case 'running':
      return 'processing'
    case 'completed':
      return 'success'
    case 'failed':
      return 'error'
    default:
      return 'default'
  }
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'running':
      return 'Kör...'
    case 'completed':
      return 'Klar'
    case 'failed':
      return 'Misslyckades'
    default:
      return status
  }
}

const SKIP_COLUMNS = new Set(['flags', 'utförare_namn', 'personal'])

const formatColumnTitle = (key: string): string => {
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')
}

const formatCellValue = (val: unknown): string => {
  if (val === null || val === undefined) return ''
  if (Array.isArray(val)) {
    return val.map((item) => (typeof item === 'object' && item !== null ? JSON.stringify(item) : String(item))).join(', ')
  }
  if (typeof val === 'object') return JSON.stringify(val)
  return String(val)
}

const isComplexColumn = (data: Record<string, unknown>[], key: string): boolean => {
  return data.some((row) => {
    const val = row[key]
    return Array.isArray(val) && val.length > 0 && typeof val[0] === 'object'
  })
}

const buildColumns = (data: Record<string, unknown>[]): ColumnsType<Record<string, unknown>> => {
  if (data.length === 0) return []
  const allKeys = new Set<string>()
  data.forEach((row) => Object.keys(row).forEach((k) => allKeys.add(k)))

  return Array.from(allKeys)
    .filter((key) => !SKIP_COLUMNS.has(key) && !isComplexColumn(data, key))
    .map((key) => ({
      title: formatColumnTitle(key),
      dataIndex: key,
      key,
      render: (val: unknown) => formatCellValue(val),
      width: 150,
    }))
}

const convertToCSV = (data: Record<string, unknown>[]) => {
  if (data.length === 0) return ''
  const headers = Object.keys(data[0])
  const rows = data.map((row) =>
    headers
      .map((h) => {
        const val = row[h]
        if (Array.isArray(val)) return `"${val.join(', ')}"`
        if (typeof val === 'string' && val.includes(',')) return `"${val}"`
        return val ?? ''
      })
      .join(',')
  )
  return [headers.join(','), ...rows].join('\n')
}

const FlagRunResults = ({ runId }: { runId: number }) => {
  const { data: run } = useRunStatus(runId)

  if (!run) return <Spin />

  const runData = run as any
  const results = Array.isArray(runData.results) ? runData.results : []

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(results, null, 2))
    message.success('Kopierat till urklipp')
  }

  const handleCSV = () => {
    if (results.length === 0) return
    const csv = convertToCSV(results)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `flagg-resultat-${runId}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <span>{runData.flagName}</span>
          <Tag color={getStatusColor(runData.status)}>{getStatusLabel(runData.status)}</Tag>
          {runData.resultCount !== null && <span>{runData.resultCount} träffar</span>}
        </Space>
      }
      extra={
        results.length > 0 && (
          <Space>
            <Button size="small" onClick={handleCopy}>
              Kopiera JSON
            </Button>
            <Button size="small" onClick={handleCSV}>
              Exportera CSV
            </Button>
          </Space>
        )
      }
    >
      {runData.error && <p style={{ color: 'red' }}>{runData.error}</p>}

      {results.length > 0 && (
        <Table columns={buildColumns(results)} dataSource={results} rowKey={(_, i) => String(i)} size="small" bordered scroll={{ x: 'max-content', y: 400 }} pagination={{ pageSize: 50 }} />
      )}
    </Card>
  )
}

export default FlagRunResults
